import Compressor from 'compressorjs';

export interface UploadedFile
{
    id: number;
    url: string;
    name: string;
    upload: File;
    thumb: any;
}

interface PushResult<T = UploadedFile, K = string> {
    result: Array<T>;
    rejected: Array<K>
}

interface RejectedFile {
    type?: string,
    name?: string
}

export default class FileUploadHelper
{
    private files = [] as UploadedFile[];
    private extensions = [] as string[];
    private accept = [] as string [];

    public constructor(files: UploadedFile[], extensions: string[], accept: string[])
    {
        this.files = files;
        this.extensions = extensions;
        this.accept = accept;
    }

    public setFiles(files: UploadedFile[]): void
    {
        this.files = files;
    }

    public async pushFiles(files: File []): Promise<PushResult>
    {
        const result: UploadedFile[] = [];
        const rejected: string[] = [];

        this.files.map(p => result.push(p));

        for (let i = 0; i < files.length; i++)
        {
            const file = await this.compressImg(files[i]);
            const ext = file.name.toLowerCase().split('.').pop();

            if (this.accept.length > 0 && this.accept.indexOf(file.type) === -1)
            {
                rejected.push(file.name);
            }
            else if (this.extensions.length > 0 && this.extensions.indexOf(ext) === -1)
            {
                rejected.push(file.name);
            }
            else
            {
                result.push({
                    id: 0,
                    url: '',
                    name: file.name,
                    upload: file,
                    thumb: await this.getThumb(file)
                });
            }
        }

        return {
            result,
            rejected
        };
    }

    public async getThumb(file: File): Promise<string>
    {
        return new Promise(resolve =>
        {
            const mimeParts = file.type.split('/');

            if (mimeParts[0] === 'image')
            {
                const reader = new FileReader();

                reader.onload = (e) =>
                {
                    const image = new Image();

                    image.onload = () =>
                    {
                        const canvas = document.createElement("canvas");
                        const context = canvas.getContext("2d");
                        const height = 150;
                        const width = (height * image.width) / image.height;

                        canvas.width = width;
                        canvas.height = height;
                        context.drawImage(image,
                            0,
                            0,
                            image.width,
                            image.height,
                            0,
                            0,
                            canvas.width,
                            canvas.height
                        );

                        resolve(canvas.toDataURL());
                    };

                    image.src = (e.target as FileReader).result.toString();
                };

                reader.readAsDataURL(file);
            }
            else
            {
                resolve(null);
            }
        });
    }

    public async compressImg(imgToCompress: File): Promise<File>
    {
        if (!imgToCompress.type.includes('image/'))
        {
            return imgToCompress;
        }

        let imgQuality = 0.4;

        if (imgToCompress.type === 'image/png')
        {
            imgQuality = 0.80;
        }

        return await new Promise<File>((resolve, reject) =>
        {
            new Compressor(imgToCompress, {
                quality: imgQuality,
                mimeType: 'image/jpeg',
                maxWidth: 2000,
                maxHeight: 2000,
                success(result: File)
                {
                    const compressedFile = new File([result], result.name, { type: result.type });

                    resolve(compressedFile);
                },
                error(err)
                {
                    reject(err);
                },
            });
        });
    }

    public handleClipboardImages(items: DataTransferItemList): PushResult<File, RejectedFile>
    {
        const result = [] as File[];
        const rejected = [] as RejectedFile[];

        for (let i = 0; i < items.length; i++)
        {
            if (items[i].kind == "file" && (items[i].type == "image/png" || items[i].type == "image/jpeg"))
            {
                const imageFile = items[i].getAsFile();

                result.push(imageFile);
            }
            else if (items[i].kind == "file")
            {
                const file = items[i].getAsFile();

                if (file.type)
                    rejected.push({ name: file.name, type: file.type });
                else
                    rejected.push({ name: file.name });
            }
        }

        return {
            result,
            rejected
        };
    }
}
